import { ButtonLink } from '@/components/ui/button';
import { CheckCircle2, KeyRound } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Page } from '@/components/layout/page';
import { EntitlementSummary } from '@/components/payment/entitlement-summary';
import { DevNote } from '@/components/ui/dev-note';
import { Alert, Card, CardBody, Container, Section, Spinner } from '@/components/ui/primitives';
import { useSession } from '@/hooks/use-session';
import { paths } from '@/lib/paths';

/**
 * Écran de confirmation, au retour du tunnel de paiement.
 *
 * Le droit d'accès a normalement déjà été reçu : la réponse du claim
 * (`POST /payment/claim`) porte le profil **et** l'entitlement, exactement
 * comme `GET /me`. Cet écran n'a donc rien à recharger pour s'afficher.
 *
 * Il relit tout de même `GET /entitlement` une fois, pour deux raisons :
 *
 * - le webhook Stripe peut avoir enrichi l'entitlement entre-temps (marque et
 *   4 derniers chiffres de la carte, date de renouvellement) ;
 * - c'est l'occasion de vérifier que `has_access` est bien passé à `true`
 *   côté serveur, et pas seulement dans l'état local du navigateur.
 *
 * L'élément `data-dashlite="access-granted"` est le repère attendu par le
 * robot de vérification de mise en production : sa présence signe la réussite
 * du tunnel.
 */
export default function CheckoutSuccessPage() {
    const { user, entitlement, refreshEntitlement, isAuthenticated } = useSession();
    const [refreshing, setRefreshing] = useState(true);

    // Garde contre le double montage de `StrictMode` en développement.
    const refreshStarted = useRef(false);

    useEffect(() => {
        if (refreshStarted.current) {
            return;
        }

        refreshStarted.current = true;
        void refreshEntitlement().finally(() => setRefreshing(false));
    }, [refreshEntitlement]);

    // Arrivée directe sur l'URL, sans être passé par le tunnel.
    if (!isAuthenticated && !refreshing) {
        return <Navigate to={paths.pricing} replace />;
    }

    /**
     * Le contexte d'achat attaché au paiement revient sur le droit d'accès :
     * on s'en sert pour ramener le visiteur exactement là où il était.
     */
    const registrationNumber = entitlement?.metadata?.registration_number ?? null;

    return (
        <Page title="Paiement confirmé">
            <Section>
                <Container className="max-w-3xl space-y-8">
                    {/* Repère du robot de vérification de mise en production :
                        il attend qu'un élément portant cet attribut devienne
                        visible pour conclure que le tunnel a abouti. Il est
                        posé sur le bloc de confirmation lui-même, donc sur du
                        contenu réellement affiché. */}
                    <div data-dashlite="access-granted" className="text-center">
                        <span className="bg-positive-soft text-positive mx-auto flex size-16 items-center justify-center rounded-full">
                            <CheckCircle2 className="size-8" aria-hidden />
                        </span>

                        <h1 className="font-display mt-6 text-4xl tracking-tight">Votre accès est ouvert</h1>

                        <p className="text-ink-soft mt-3">
                            Merci{user?.email ? `, ${user.email}` : ''}. Le paiement a été confirmé et votre droit
                            d'accès est actif.
                        </p>
                    </div>

                    {refreshing && !entitlement ? (
                        <Card>
                            <CardBody className="flex justify-center py-10">
                                <Spinner label="Vérification du droit d'accès…" />
                            </CardBody>
                        </Card>
                    ) : entitlement ? (
                        <EntitlementSummary entitlement={entitlement} />
                    ) : null}

                    <Alert tone="info" title="Choisissez votre mot de passe" >
                        <p className="flex items-start gap-2">
                            <KeyRound className="mt-0.5 size-4 shrink-0" aria-hidden />
                            <span>
                                Un email vient de partir à votre adresse : il contient un lien pour définir votre mot
                                de passe. Vous pourrez ensuite vous reconnecter à tout moment. Vous n'en avez pas
                                besoin dans l'immédiat — votre session est déjà ouverte.
                            </span>
                        </p>
                    </Alert>

                    <div className="flex flex-wrap justify-center gap-3">
                        {registrationNumber ? (
                            <ButtonLink to={paths.company(registrationNumber)} size="lg">
                                Consulter la fiche demandée
                            </ButtonLink>
                        ) : (
                            <ButtonLink to={paths.search} size="lg">
                                Rechercher une entreprise
                            </ButtonLink>
                        )}

                        <ButtonLink to={paths.account} variant="secondary" size="lg">
                            Mon compte
                        </ButtonLink>
                    </div>

                    <DevNote
                        title="Ce que fait cet écran"
                        endpoints={['POST /payment/claim (écran précédent)', 'GET /entitlement']}
                    >
                        <p>
                            Le claim a renvoyé un <code className="font-mono">token</code> et le profil complet : le
                            visiteur est connecté sans jamais avoir saisi de mot de passe.
                        </p>
                        <p>
                            Le droit d'accès est ouvert par le webhook Stripe <em>ou</em> par le claim, selon lequel
                            arrive en premier — jamais par le simple retour du navigateur. En développement local, où
                            le webhook n'est pas livré, le claim fait foi.
                        </p>
                        {registrationNumber && (
                            <p>
                                Le contexte d'achat envoyé à <code className="font-mono">POST /payment</code> est
                                revenu dans <code className="font-mono">entitlement.metadata</code> :{' '}
                                <code className="font-mono">registration_number = {registrationNumber}</code>. C'est ce
                                qui permet de ramener le visiteur sur la bonne fiche.
                            </p>
                        )}
                    </DevNote>
                </Container>
            </Section>
        </Page>
    );
}
